import { useFormikContext } from 'formik';
import Title from '@components/layouts/Truva/Tourbook/Title';
import classnames from 'classnames';
import { CustomIcon } from '@components/shared';
import { PARAMETERS } from '@root/tracking/constants';
import TourbookMosaicGallery from '@components/layouts/Truva/Tourbook/TourbookMosaicGallery';
import { useTranslation } from 'react-i18next';
import { uniqueId } from 'lodash';
import { askingRentInMoneyPerAreaPerPeriod, askingRentInMoneyPerPeriod } from '../helpers';
import { FormValues } from '..';
import ListingAttributes from '../../Tourbook/ListingAttributes';
import ListingDescription from '../../Tourbook/ListingDescription';
import s from './TourbookExternalListingCard.module.less';

type TourbookExternalListingCardProps = {
  readOnly: boolean;
  isPreview?: boolean;
};

export const TourbookExternalListingCard = ({
  readOnly,
  isPreview = false,
}: TourbookExternalListingCardProps) => {
  const { t } = useTranslation('tourbook');

  const { values } = useFormikContext<FormValues>();

  const onOpenDirections = () => {
    // trackMapDirectionsClicked(listing.analyticsInformation);
  };

  const openMapDirections = location => {
    const baseMapsURL = `https://www.google.com/maps/dir/?api=1&destination=`;
    const url = `${baseMapsURL}${location.latitude},${location.longitude}`;
    onOpenDirections();
    window.open(url, '_blank');
  };

  const mapButton = location => {
    if (!location.latitude || !location.longitude) {
      return '';
    }

    return (
      <CustomIcon
        className={s.directionsIcon}
        data-testid="directions-icon"
        type="location"
        onClick={() => openMapDirections(location)}
      />
    );
  };

  const shownListingName = () => {
    if (values.addressLine1 !== '') {
      return values.addressLine1;
    }

    return t('externalListing.addressLabel');
  };

  const shownPrimaryName = () => {
    if (values.buildingName === null) {
      return values.addressLine1;
    }

    if (values.buildingName === '') {
      return t('externalListing.buildingName');
    }

    return values.buildingName;
  };

  const shownAddressLine2 = () => {
    if (values.addressLine2 === null) {
      return '';
    }

    if (values.addressLine2 === '') {
      return t('externalListing.address2Label');
    }

    return values.addressLine2;
  };

  const displayAddress = values.buildingName !== null ? shownListingName() : '';
  const primaryName = (shownPrimaryName() as string) || t('externalListing.addressLabel');
  const listingName = shownAddressLine2() as string;
  const state = values.state || t('externalListing.stateLabel');
  const city = values.city || t('externalListing.cityLabel');
  const zipCode = values.zipCode || t('externalListing.zipLabel');
  const { latitude, longitude } = values;

  // tell the child component which attributes should have preview styling
  // by detecting if we are using the placeholder values
  const previewStatus = {
    submarket: !values.submarket,
    addressLine1:
      primaryName === t('externalListing.addressLabel') ||
      primaryName === t('externalListing.buildingName'),
    addressLine2: displayAddress === t('externalListing.addressLabel'),
    addressLine3: listingName === t('externalListing.address2Label'),
    cityState: city === t('externalListing.cityLabel') && state === t('externalListing.stateLabel'),
  };

  return (
    <div className={s.container}>
      <div className={s.titleLinkAndDeleteIcon}>
        <Title
          isPreview={isPreview}
          primaryName={primaryName}
          displayAddress={displayAddress}
          listingName={listingName}
          cityLocalityRegion={`${city}, ${state} ${zipCode}`}
          countryCode="US"
          neighborhood={values.submarket || t('externalListing.submarketLabel')}
          directionsButton={mapButton({ latitude, longitude })}
          previewStatus={previewStatus}
        />
      </div>
      <div className={s.mosaicGalleryContainer}>
        <TourbookMosaicGallery
          photos={values.photos}
          video={null}
          sourceContent={PARAMETERS.tourbookListingGallery}
          sourcePage={PARAMETERS.tourbookPage}
          noLightbox
        />
      </div>
      <ListingAttributes
        askingRentInMoneyPerAreaPerPeriod={askingRentInMoneyPerAreaPerPeriod(
          values.askingRent,
          values.askingRentUnits as 'Usd/sf/yr' | 'Usd/Mo',
          values.size,
        )}
        askingRentInMoneyPerPeriod={askingRentInMoneyPerPeriod(
          values.askingRent,
          values.askingRentUnits as 'Usd/sf/yr' | 'Usd/Mo',
          values.size,
        )}
        condition={values.condition}
        leaseType={values.leaseType}
        dateAvailable={values.availabilityDate}
        availableToday={values.availableImmediately}
        minMaxArea={null}
        size={
          values.size
            ? { magnitude: values.size, type: 'area', units: { area: 'SquareFoot' } }
            : null
        }
        showNullAskingRent
      />
      <ListingDescription
        className={classnames(!values.description && s.preview)}
        description={values.description || t('externalListing.description')}
        readOnly={readOnly}
        tourbookListingId={uniqueId()}
      />
    </div>
  );
};

export default TourbookExternalListingCard;
